import React, { createContext, useEffect, useReducer } from 'react'
import jwtDecode from 'jwt-decode'
import axios from 'axios.js'
import { MatxLoading } from 'app/components'
import { useDispatch } from 'react-redux'
import { authLogin } from 'app/redux/action'

const initialState = {
    isAuthenticated: false,
    isInitialised: false,
    user: null,
    adminInfo: null,
    sessionInfo: null,
}

const isValidToken = (accessToken) => {
    if (!accessToken) {
        return false
    }

    const decodedToken = jwtDecode(accessToken)

    const currentTime = Date.now() / 1000
    return decodedToken.exp > currentTime
}

const setSession = (accessToken, user, adminInfo, sessionInfo) => {
    if (accessToken) {
        localStorage.setItem('accessToken', accessToken)
        localStorage.setItem('authData', JSON.stringify(user))
        localStorage.setItem('adminInfo', JSON.stringify(adminInfo))
        localStorage.setItem('sessionInfo', JSON.stringify(sessionInfo))
        axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`
    } else {
        localStorage.removeItem('accessToken')
        localStorage.removeItem('authData')
        localStorage.removeItem('adminInfo')
        localStorage.removeItem('sessionInfo')
        delete axios.defaults.headers.common.Authorization
    }
}

const reducer = (state, action) => {
    switch (action.type) {
        case 'INIT': {
            const { isAuthenticated, user, adminInfo, sessionInfo } =
                action.payload

            return {
                ...state,
                isAuthenticated,
                isInitialised: true,
                user,
                adminInfo,
                sessionInfo,
            }
        }
        case 'LOGIN': {
            const { adminInfo, user, sessionInfo } = action.payload

            return {
                ...state,
                isAuthenticated: true,
                user: user,
                adminInfo: adminInfo,
                sessionInfo: sessionInfo,
            }
        }
        case 'LOGOUT': {
            return {
                ...state,
                isAuthenticated: false,
                user: null,
                sessionInfo: null,
                adminInfo: null,
            }
        }
        case 'REGISTER': {
            const { user } = action.payload

            return {
                ...state,
                isAuthenticated: true,
                user,
            }
        }
        default: {
            return { ...state }
        }
    }
}

const AuthContext = createContext({
    ...initialState,
    method: 'JWT',
    login: () => Promise.resolve(),
    logout: () => {},
    register: () => Promise.resolve(),
})

export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState)

    const login = async (email, password) => {
        const response = await axios.post('/register/login-admin', {
            id: email,
            pin: password,
        })

        const { user, roles, status, token, adminInfo, sessionInfo } =
            response.data

        if (!status) {
            return false
        }

        const userUpdate = { ...user, roles }

        setSession(token, userUpdate, adminInfo, sessionInfo)
        dispatchAuth(authLogin(userUpdate))

        dispatch({
            type: 'LOGIN',
            payload: {
                user: userUpdate,
                adminInfo: adminInfo,
                sessionInfo: sessionInfo,
            },
        })

        return true
    }

    const register = async (email, username, password) => {
        const response = await axios.post('/api/auth/register', {
            email,
            username,
            password,
        })

        const { accessToken, user } = response.data

        setSession(accessToken)

        dispatch({
            type: 'REGISTER',
            payload: {
                user,
            },
        })
    }

    const logout = () => {
        setSession(null, null, null, null)
        dispatch({ type: 'LOGOUT' })
        dispatchAuth(authLogin(undefined))
    }

    const dispatchAuth = useDispatch()

    useEffect(() => {
        ;(async () => {
            try {
                const accessToken = window.localStorage.getItem('accessToken')

                if (accessToken && isValidToken(accessToken)) {
                    const user = JSON.parse(
                        window.localStorage.getItem('authData')
                    )
                    const adminInfo = JSON.parse(
                        window.localStorage.getItem('adminInfo')
                    )

                    const sessionInfo = JSON.parse(
                        window.localStorage.getItem('sessionInfo')
                    )

                    //const sessionInfo =                        window.localStorage.getItem('activeSessionId')
                    setSession(accessToken, user, adminInfo, sessionInfo)

                    dispatchAuth(authLogin(user))

                    dispatch({
                        type: 'INIT',
                        payload: {
                            isAuthenticated: true,
                            user,
                            adminInfo,
                            sessionInfo,
                        },
                    })
                } else {
                    dispatch({
                        type: 'INIT',
                        payload: {
                            isAuthenticated: false,
                            user: null,
                            adminInfo: null,
                            sessionInfo: null,
                        },
                    })
                }
            } catch (err) {
                console.error(err)
                dispatch({
                    type: 'INIT',
                    payload: {
                        isAuthenticated: false,
                        user: null,
                        adminInfo: null,
                        sessionInfo: null,
                    },
                })
            }
        })()
    }, [])

    if (!state.isInitialised) {
        return <MatxLoading />
    }

    return (
        <AuthContext.Provider
            value={{
                ...state,
                method: 'JWT',
                login,
                logout,
                register,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export default AuthContext
